import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  FormControlLabel,
  InputAdornment,
  Chip,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ru } from "date-fns/locale";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  FILE_SIZE_LIMIT_BYTES,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";
import { debounce } from "../../../shared/lib/debounce";
import {
  useGetBannersQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useGetBannerImagesQuery,
  useUploadBannerImageMutation,
  useDeleteBannerImageMutation,
  useGetClickActionsQuery,
  useGetBannerTargetingRulesQuery,
  useCreateTargetingRuleMutation,
  // useUpdateTargetingRuleMutation,
  useDeleteTargetingRuleMutation,
  Banner,
  useDeleteBannerMutation,
} from "../../../entities/admin-banner";
import { format } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/Upload";
import ImageIcon from "@mui/icons-material/Image";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import LinkIcon from "@mui/icons-material/Link";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import {
  useGetNewsQuery,
  useGetNewsByIdQuery,
  News,
} from "../../../entities/admin-news";
import {
  useGetEventsQuery,
  useGetEventQuery,
  EventListItem,
} from "../../../entities/admin-events";

// Interface for BannerCard props
interface BannerCardProps {
  banner: Banner;
  onEdit: (banner: Banner) => void;
  onDelete: (banner: Banner) => void;
  onManageImages: (banner: Banner) => void;
  onManageTargeting: (banner: Banner) => void;
}

// BannerCard component for displaying each banner
const BannerCard = ({
  banner,
  onEdit,
  onDelete,
  onManageImages,
  onManageTargeting,
}: BannerCardProps) => {
  const [imageIndex, setImageIndex] = useState(0);
  const bannerImages = banner.images;
  const isActive =
    new Date(banner.startAt) <= new Date() &&
    new Date(banner.endAt) >= new Date();

  const handleNextImage = () => {
    if (bannerImages.length > 1) {
      setImageIndex((prev) => (prev + 1) % bannerImages.length);
    }
  };

  const handlePrevImage = () => {
    if (bannerImages.length > 1) {
      setImageIndex(
        (prev) => (prev - 1 + bannerImages.length) % bannerImages.length
      );
    }
  };

  const currentImage = bannerImages[imageIndex];

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: isActive ? "4px solid #4caf50" : "none",
      }}
    >
      {bannerImages.length > 0 ? (
        <Box sx={{ position: "relative", height: 220 }}>
          <CardMedia
            component="img"
            height="220"
            image={currentImage?.url}
            alt={banner.title}
            sx={{ objectFit: "contain", bgcolor: "#f5f5f5" }}
          />
          {bannerImages.length > 1 && (
            <>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 8,
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0,0,0,0.3)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
                }}
                onClick={handlePrevImage}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 8,
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0,0,0,0.3)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
                }}
                onClick={handleNextImage}
              >
                <NavigateNextIcon />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                  px: 1,
                  borderRadius: 1,
                  fontSize: 12,
                }}
              >
                {imageIndex + 1} / {bannerImages.length}
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f5f5f5",
          }}
        >
          <Typography color="text.secondary">Нет изображений</Typography>
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="div">
            {banner.title}
            {isActive && (
              <Chip
                label="Активен"
                color="success"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Приоритет: {banner.rang}
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Статус:
            </Typography>
            <Typography variant="body2">
              {isActive
                ? "Активен"
                : new Date(banner.startAt) > new Date()
                ? "Ожидает активации"
                : "Завершен"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Дата создания:
            </Typography>
            <Typography variant="body2">
              {format(new Date(banner.createdAt), "dd.MM.yyyy HH:mm")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Начало показа:
            </Typography>
            <Typography variant="body2">
              {format(new Date(banner.startAt), "dd.MM.yyyy HH:mm")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Окончание показа:
            </Typography>
            <Typography variant="body2">
              {format(new Date(banner.endAt), "dd.MM.yyyy HH:mm")}
            </Typography>
          </Box>
          {banner.clickAction && (
            <Box sx={{ gridColumn: "span 2" }}>
              <Typography variant="body2" color="text.secondary">
                Действие при клике:
              </Typography>
              <Typography variant="body2">
                {banner.clickAction.name}
                {banner.clickActionParams && (
                  <Tooltip title={banner.clickActionParams}>
                    <Chip
                      size="small"
                      label="Параметры"
                      variant="outlined"
                      icon={<LinkIcon />}
                      sx={{ ml: 1 }}
                    />
                  </Tooltip>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-end", p: 2, pt: 0 }}>
        <Button
          startIcon={<ImageIcon />}
          size="small"
          onClick={() => onManageImages(banner)}
        >
          Изображения
        </Button>
        <Button
          startIcon={<GpsFixedIcon />}
          size="small"
          onClick={() => onManageTargeting(banner)}
        >
          Таргетинг
        </Button>
        <IconButton size="small" onClick={() => onEdit(banner)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(banner)}>
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
};

const BannersManagement = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedBannerForAction, setSelectedBannerForAction] =
    useState<Banner | null>(null);

  // filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [displayActive, setDisplayActive] = useState<boolean | undefined>(
    undefined
  );
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [pageSize] = useState(10);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openImagesDialog, setOpenImagesDialog] = useState(false);
  const [openTargetingDialog, setOpenTargetingDialog] = useState(false);
  const [openNewsDialog, setOpenNewsDialog] = useState(false);
  const [openEventsDialog, setOpenEventsDialog] = useState(false);

  // Form states
  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerRang, setBannerRang] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(
    new Date(new Date().setFullYear(new Date().getFullYear() + 1))
  );
  const [selectedClickAction, setSelectedClickAction] = useState<number | null>(
    null
  );
  const [clickActionParams, setClickActionParams] = useState("");
  const [selectedNewsId, setSelectedNewsId] = useState<number | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [newsSearchTerm, setNewsSearchTerm] = useState("");
  const [eventsSearchTerm, setEventsSearchTerm] = useState("");
  const [debouncedNewsSearchTerm, setDebouncedNewsSearchTerm] = useState("");
  const [debouncedEventsSearchTerm, setDebouncedEventsSearchTerm] =
    useState("");

  // Infinite scroll states
  const [allNews, setAllNews] = useState<News[]>([]);
  const [allEvents, setAllEvents] = useState<EventListItem[]>([]);
  const [newsPageToken, setNewsPageToken] = useState<string | undefined>(
    undefined,
  );
  const [eventsPageToken, setEventsPageToken] = useState<string | undefined>(
    undefined,
  );
  const [isLoadingMoreNews, setIsLoadingMoreNews] = useState(false);
  const [isLoadingMoreEvents, setIsLoadingMoreEvents] = useState(false);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);

  // Targeting rule state
  const [newTargetingRole, setNewTargetingRole] = useState<
    "guest" | "user" | "resident"
  >("guest");

  // Queries and mutations
  const {
    data: bannersData,
    isLoading: isLoadingBanners,
    refetch,
  } = useGetBannersQuery({
    search: searchTerm,
    display: displayActive,
    sort: sortField,
    order: sortOrder,
    limit: pageSize,
    pageToken,
  });

  // Fetch banner images for the dialog
  const { data: bannerImagesData, isLoading: isLoadingImages } =
    useGetBannerImagesQuery(selectedBannerForAction?.id || 0, {
      skip: !selectedBannerForAction || !openImagesDialog,
    });

  // Fetch click actions
  const { data: clickActionsData } = useGetClickActionsQuery();

  // Debounced search handlers
  const debouncedSetNewsSearch = useCallback((query: string) => {
    const debouncedFn = debounce((q: string) => {
      setDebouncedNewsSearchTerm(q);
    }, 500);
    debouncedFn(query);
  }, []);

  const debouncedSetEventsSearch = useCallback((query: string) => {
    const debouncedFn = debounce((q: string) => {
      setDebouncedEventsSearchTerm(q);
    }, 500);
    debouncedFn(query);
  }, []);

  // Update debounced search terms
  useEffect(() => {
    debouncedSetNewsSearch(newsSearchTerm);
  }, [newsSearchTerm, debouncedSetNewsSearch]);

  useEffect(() => {
    debouncedSetEventsSearch(eventsSearchTerm);
  }, [eventsSearchTerm, debouncedSetEventsSearch]);

  // Fetch news and events for the click actions (for dialog)
  const { data: newsData, isLoading: isLoadingNews } = useGetNewsQuery(
    {
      order: "asc",
      search: debouncedNewsSearchTerm || undefined,
      pageToken: newsPageToken,
      limit: 50,
    },
    {
      skip: !openNewsDialog,
    }
  );

  const { data: eventsData, isLoading: isLoadingEvents } = useGetEventsQuery(
    {
      order: "asc",
      search: debouncedEventsSearchTerm || undefined,
      pageToken: eventsPageToken,
      limit: 50,
    },
    {
      skip: !openEventsDialog,
    }
  );

  // Fetch selected news and event to show their titles
  const { data: selectedNewsData } = useGetNewsByIdQuery(selectedNewsId || 0, {
    skip: !selectedNewsId || selectedClickAction !== 2,
  });

  const { data: selectedEventData } = useGetEventQuery(selectedEventId || 0, {
    skip: !selectedEventId || selectedClickAction !== 4,
  });

  // Fetch targeting rules for the selected banner
  const { data: targetingRulesData, isLoading: isLoadingTargetingRules } =
    useGetBannerTargetingRulesQuery(selectedBannerForAction?.id || 0, {
      skip: !selectedBannerForAction || !openTargetingDialog,
    });

  const [createBanner, { isLoading: isCreating }] = useCreateBannerMutation();
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation();
  const [uploadImage, { isLoading: isUploading }] =
    useUploadBannerImageMutation();
  const [deleteImage, { isLoading: isDeleting }] =
    useDeleteBannerImageMutation();
  const [createTargetingRule, { isLoading: isCreatingRule }] =
    useCreateTargetingRuleMutation();
  // const [updateTargetingRule] =
  // useUpdateTargetingRuleMutation();
  const [deleteTargetingRule, { isLoading: isDeletingRule }] =
    useDeleteTargetingRuleMutation();
  const [deleteBanner] = useDeleteBannerMutation();

  // Handlers
  const handleOpenCreateDialog = () => {
    setBannerTitle("");
    setBannerRang(0);
    setStartDate(new Date());
    setEndDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
    setSelectedClickAction(null);
    setClickActionParams("");
    setSelectedNewsId(null);
    setSelectedEventId(null);
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (banner: Banner) => {
    setSelectedBannerForAction(banner);
    setBannerTitle(banner.title);
    setBannerRang(banner.rang);
    setStartDate(new Date(banner.startAt));
    setEndDate(new Date(banner.endAt));
    setSelectedClickAction(banner.clickAction?.id || null);
    setClickActionParams(banner.clickActionParams || "");

    // Reset news and event selections
    setSelectedNewsId(null);
    setSelectedEventId(null);

    // Set selected news or event based on clickActionParams if it exists
    if (banner.clickAction && banner.clickActionParams) {
      const actionName = banner.clickAction.id;
      const paramId = parseInt(banner.clickActionParams);

      if (actionName === 2 && !isNaN(paramId)) {
        setSelectedNewsId(paramId);
      } else if (actionName === 4 && !isNaN(paramId)) {
        setSelectedEventId(paramId);
      }
    }

    setOpenEditDialog(true);
  };

  const handleOpenImagesDialog = (banner: Banner) => {
    setSelectedBannerForAction(banner);
    setOpenImagesDialog(true);
  };

  const handleOpenTargetingDialog = (banner: Banner) => {
    setSelectedBannerForAction(banner);
    setNewTargetingRole("guest");
    setOpenTargetingDialog(true);
  };

  const handleOpenDeleteDialog = (banner: Banner) => {
    setSelectedBannerForAction(banner);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenImagesDialog(false);
    setOpenTargetingDialog(false);
    setOpenNewsDialog(false);
    setOpenEventsDialog(false);
    setNewsSearchTerm("");
    setEventsSearchTerm("");
  };

  const handleSearch = () => {
    setPageToken(undefined);
    refetch();
  };

  const handleNextBannersPage = () => {
    const next = bannersData?.pageToken?.trim();
    if (next) {
      setPageToken(next);
    }
  };

  const handlePreviousBannersPage = () => {
    setPageToken(undefined);
  };

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPageToken(undefined);
  };

  const isFormValid = () => {
    return bannerTitle.trim() !== "" && startDate && endDate;
  };

  const handleCreateBanner = async () => {
    if (!isFormValid()) return;

    try {
      await createBanner({
        title: bannerTitle.trim(),
        rang: bannerRang,
        startAt: startDate!.toISOString(),
        endAt: endDate!.toISOString(),
        clickActionId: selectedClickAction || undefined,
        clickActionParams: clickActionParams.trim() || undefined,
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating banner:", error);
    }
  };

  const handleUpdateBanner = async () => {
    if (!selectedBannerForAction || !isFormValid()) return;

    try {
      await updateBanner({
        id: selectedBannerForAction.id,
        body: {
          title: bannerTitle.trim(),
          rang: bannerRang,
          startAt: startDate!.toISOString(),
          endAt: endDate!.toISOString(),
          clickActionId: selectedClickAction || undefined,
          clickActionParams: clickActionParams.trim() || undefined,
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating banner:", error);
    }
  };

  const handleDeleteBanner = async () => {
    if (!selectedBannerForAction) return;

    try {
      await deleteBanner(selectedBannerForAction.id).unwrap();
    } catch (error) {
      console.error("Error deleting banner:", error);
    }
    handleCloseDialogs();
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (
      !selectedBannerForAction ||
      !event.target.files ||
      event.target.files.length === 0
    )
      return;

    const file = event.target.files[0];
    const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
    if (sizeError) {
      alert(sizeError);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      await uploadImage({
        id: selectedBannerForAction.id,
        file: formData,
      }).unwrap();

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!selectedBannerForAction) return;

    try {
      await deleteImage({
        id: selectedBannerForAction.id,
        imageId,
      }).unwrap();
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  const handleCreateTargetingRule = async () => {
    if (!selectedBannerForAction) return;

    try {
      await createTargetingRule({
        id: selectedBannerForAction.id,
        body: {
          role: newTargetingRole,
        },
      }).unwrap();
      setNewTargetingRole("guest");
    } catch (error) {
      console.error("Error creating targeting rule:", error);
    }
  };

  // const handleUpdateTargetingRule = async (
  //   ruleId: number,
  //   role: "guest" | "user" | "resident"
  // ) => {
  //   if (!selectedBannerForAction) return;

  //   try {
  //     await updateTargetingRule({
  //       id: selectedBannerForAction.id,
  //       ruleId,
  //       body: {
  //         role,
  //       },
  //     }).unwrap();
  //   } catch (error) {
  //     console.error("Error updating targeting rule:", error);
  //   }
  // };

  const handleDeleteTargetingRule = async (ruleId: number) => {
    if (!selectedBannerForAction) return;

    try {
      await deleteTargetingRule({
        id: selectedBannerForAction.id,
        ruleId,
      }).unwrap();
    } catch (error) {
      console.error("Error deleting targeting rule:", error);
    }
  };

  // Update handler for click action changes
  const handleClickActionChange = (actionId: number | "") => {
    const value = actionId === "" ? null : Number(actionId);
    setSelectedClickAction(value);

    // Reset related values when changing click action
    setClickActionParams("");
    setSelectedNewsId(null);
    setSelectedEventId(null);
  };

  // Handler for news selection
  const handleNewsSelection = (newsId: number) => {
    setSelectedNewsId(newsId);
    setClickActionParams(newsId.toString());
    setOpenNewsDialog(false);
    setNewsSearchTerm("");
  };

  // Handler for event selection
  const handleEventSelection = (eventId: number) => {
    setSelectedEventId(eventId);
    setClickActionParams(eventId.toString());
    setOpenEventsDialog(false);
    setEventsSearchTerm("");
  };

  // Handler to open news dialog
  const handleOpenNewsDialog = () => {
    setNewsSearchTerm("");
    setAllNews([]);
    setNewsPageToken(undefined);
    setHasMoreNews(true);
    setOpenNewsDialog(true);
  };

  // Handler to open events dialog
  const handleOpenEventsDialog = () => {
    setEventsSearchTerm("");
    setAllEvents([]);
    setEventsPageToken(undefined);
    setHasMoreEvents(true);
    setOpenEventsDialog(true);
  };

  // Handler to clear news selection
  const handleClearNewsSelection = () => {
    setSelectedNewsId(null);
    setClickActionParams("");
  };

  // Handler to clear event selection
  const handleClearEventSelection = () => {
    setSelectedEventId(null);
    setClickActionParams("");
  };

  useEffect(() => {
    setPageToken(undefined);
  }, [searchTerm, displayActive, sortField, sortOrder]);

  // Reset and load initial news data when dialog opens or debounced search changes
  useEffect(() => {
    if (openNewsDialog) {
      setAllNews([]);
      setNewsPageToken(undefined);
      setHasMoreNews(true);
      setIsLoadingMoreNews(false);
    }
  }, [openNewsDialog, debouncedNewsSearchTerm]);

  // Reset and load initial events data when dialog opens or debounced search changes
  useEffect(() => {
    if (openEventsDialog) {
      setAllEvents([]);
      setEventsPageToken(undefined);
      setHasMoreEvents(true);
      setIsLoadingMoreEvents(false);
    }
  }, [openEventsDialog, debouncedEventsSearchTerm]);

  // Merge news data when new data arrives
  useEffect(() => {
    if (newsData?.news && openNewsDialog) {
      if (newsPageToken === undefined) {
        // First load or search changed
        setAllNews(newsData.news);
      } else {
        // Append new data (pagination)
        setAllNews((prev) => {
          // Avoid duplicates
          const existingIds = new Set(prev.map((n) => n.id));
          const newItems = newsData.news.filter((n) => !existingIds.has(n.id));
          return [...prev, ...newItems];
        });
      }

      setHasMoreNews(Boolean(newsData.pageToken?.trim()));
      setIsLoadingMoreNews(false);
    }
  }, [newsData, newsPageToken, openNewsDialog]);

  // Merge events data when new data arrives
  useEffect(() => {
    if (eventsData?.events && openEventsDialog) {
      if (eventsPageToken === undefined) {
        // First load or search changed
        setAllEvents(eventsData.events);
      } else {
        // Append new data (pagination)
        setAllEvents((prev) => {
          // Avoid duplicates
          const existingIds = new Set(prev.map((e) => e.id));
          const newItems = eventsData.events.filter(
            (e) => !existingIds.has(e.id)
          );
          return [...prev, ...newItems];
        });
      }

      setHasMoreEvents(Boolean(eventsData.pageToken?.trim()));
      setIsLoadingMoreEvents(false);
    }
  }, [eventsData, eventsPageToken, openEventsDialog]);

  // Handler to load more news
  const loadMoreNews = () => {
    const next = newsData?.pageToken?.trim();
    if (!isLoadingMoreNews && !isLoadingNews && hasMoreNews && next) {
      setIsLoadingMoreNews(true);
      setNewsPageToken(next);
    }
  };

  // Handler to load more events
  const loadMoreEvents = () => {
    const next = eventsData?.pageToken?.trim();
    if (!isLoadingMoreEvents && !isLoadingEvents && hasMoreEvents && next) {
      setIsLoadingMoreEvents(true);
      setEventsPageToken(next);
    }
  };

  // Handler for news list scroll
  const handleNewsScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const bottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    if (bottom && hasMoreNews && !isLoadingMoreNews) {
      loadMoreNews();
    }
  };

  // Handler for events list scroll
  const handleEventsScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const bottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    if (bottom && hasMoreEvents && !isLoadingMoreEvents) {
      loadMoreEvents();
    }
  };

  // Render banner cards in a grid
  const renderBannerCards = () => {
    if (!bannersData?.banners?.length) {
      return (
        <Typography color="text.secondary" align="center" py={3}>
          Баннеры не найдены
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {bannersData.banners.map((banner) => (
          <Box
            key={banner.id}
            sx={{
              width: { xs: "100%", md: "calc(50% - 1.5rem)" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <BannerCard
              banner={banner}
              onEdit={handleOpenEditDialog}
              onDelete={handleOpenDeleteDialog}
              onManageImages={handleOpenImagesDialog}
              onManageTargeting={handleOpenTargetingDialog}
            />
          </Box>
        ))}
      </Box>
    );
  };

  // Render form fields
  const renderBannerForm = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <TextField
        sx={{ mt: 1 }}
        label="Название баннера"
        fullWidth
        value={bannerTitle}
        onChange={(e) => setBannerTitle(e.target.value)}
      />
      <TextField
        label="Приоритет"
        type="number"
        fullWidth
        value={bannerRang}
        onChange={(e) => setBannerRang(parseInt(e.target.value) || 0)}
        helperText="Чем выше значение, тем выше приоритет баннера"
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
          <DateTimePicker
            label="Дата начала показа"
            value={startDate}
            onChange={setStartDate}
            sx={{ width: "100%" }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
          <DateTimePicker
            label="Дата окончания показа"
            value={endDate}
            onChange={setEndDate}
            sx={{ width: "100%" }}
          />
        </LocalizationProvider>
      </Box>

      <FormControl fullWidth>
        <InputLabel>Действие при клике</InputLabel>
        <Select
          value={selectedClickAction === null ? "" : selectedClickAction}
          label="Действие при клике"
          onChange={(e) =>
            handleClickActionChange(e.target.value as number | "")
          }
        >
          <MenuItem value="">Нет действия</MenuItem>
          {clickActionsData?.actions.map((action) => (
            <MenuItem key={action.id} value={action.id}>
              {action.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedClickAction !== null && (
        <>
          {clickActionsData?.actions.find(
            (a) => a.id === selectedClickAction && a.id === 2
          ) ? (
            <Box>
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenNewsDialog}
                  sx={{ justifyContent: "flex-start" }}
                >
                  {selectedNewsId
                    ? selectedNewsData?.news?.title || "Выбрана новость"
                    : "Выберите новость"}
                </Button>
                {selectedNewsId && (
                  <IconButton
                    size="small"
                    onClick={handleClearNewsSelection}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              {selectedNewsId && selectedNewsData?.news && (
                <Typography variant="body2" color="text.secondary">
                  Выбрана новость: {selectedNewsData.news.title}
                </Typography>
              )}
            </Box>
          ) : clickActionsData?.actions.find(
              (a) => a.id === selectedClickAction && a.id === 4
            ) ? (
            <Box>
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenEventsDialog}
                  sx={{ justifyContent: "flex-start" }}
                >
                  {selectedEventId
                    ? selectedEventData?.event?.title || "Выбрано мероприятие"
                    : "Выберите мероприятие"}
                </Button>
                {selectedEventId && (
                  <IconButton
                    size="small"
                    onClick={handleClearEventSelection}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              {selectedEventId && selectedEventData?.event && (
                <Typography variant="body2" color="text.secondary">
                  Выбрано мероприятие: {selectedEventData.event.title}
                </Typography>
              )}
            </Box>
          ) : (
            <TextField
              label="Параметры действия"
              fullWidth
              value={clickActionParams}
              onChange={(e) => setClickActionParams(e.target.value)}
              helperText="Дополнительные параметры для действия при клике (например, URL)"
            />
          )}
        </>
      )}
    </Box>
  );

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog
      open={openCreateDialog}
      onClose={handleCloseDialogs}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle textTransform="none" textAlign="center">
        Добавить баннер
      </DialogTitle>
      <DialogContent>{renderBannerForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateBanner}
          disabled={!isFormValid() || isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog
      open={openEditDialog}
      onClose={handleCloseDialogs}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Редактировать баннер</DialogTitle>
      <DialogContent>{renderBannerForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateBanner}
          disabled={!isFormValid() || isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderImagesDialog = () => (
    <Dialog
      open={openImagesDialog}
      onClose={handleCloseDialogs}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Изображения баннера "{selectedBannerForAction?.title}"
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <CircularProgress size={24} />
            ) : (
              "Загрузить изображение"
            )}
          </Button>
          <FileUploadInfo
            type="banner"
            recommendedSize="1200x600 пикселей"
          />
        </Box>

        {isLoadingImages ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : bannerImagesData?.images?.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: 2,
            }}
          >
            {bannerImagesData.images.map((image) => (
              <Card key={image.id}>
                <CardMedia
                  component="img"
                  height="200"
                  image={image.url}
                  alt={selectedBannerForAction?.title}
                  sx={{ objectFit: "contain", bgcolor: "#f5f5f5" }}
                />
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {image.width} x {image.height} px
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteImage(image.id)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? <CircularProgress size={16} /> : "Удалить"}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" align="center" py={3}>
            Нет загруженных изображений для этого баннера
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );

  const renderTargetingDialog = () => (
    <Dialog
      open={openTargetingDialog}
      onClose={handleCloseDialogs}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Правила таргетинга для баннера "{selectedBannerForAction?.title}"
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Настройте для каких пользователей будет показываться баннер
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", mt: 2, gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Роль пользователя</InputLabel>
              <Select
                value={newTargetingRole}
                label="Роль пользователя"
                onChange={(e) =>
                  setNewTargetingRole(
                    e.target.value as "guest" | "user" | "resident"
                  )
                }
              >
                <MenuItem value="guest">Гость</MenuItem>
                <MenuItem value="user">Пользователь</MenuItem>
                <MenuItem value="resident">Резидент</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleCreateTargetingRule}
              disabled={isCreatingRule}
            >
              {isCreatingRule ? (
                <CircularProgress size={24} />
              ) : (
                "Добавить правило"
              )}
            </Button>
          </Box>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
          Существующие правила
        </Typography>

        {isLoadingTargetingRules ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : targetingRulesData?.rules?.length ? (
          <List>
            {targetingRulesData.rules.map((rule) => (
              <ListItem key={rule.id}>
                <ListItemText
                  primary={
                    rule.role === "guest"
                      ? "Гость"
                      : rule.role === "user"
                      ? "Пользователь"
                      : "Резидент"
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteTargetingRule(rule.id)}
                    disabled={isDeletingRule}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary" align="center" py={2}>
            Нет правил таргетинга. Баннер будет показан всем пользователям.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );

  const renderNewsDialog = () => (
    <Dialog
      open={openNewsDialog}
      onClose={() => {
        setOpenNewsDialog(false);
        setNewsSearchTerm("");
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>Выберите новость</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2,
        }}
      >
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <TextField
            fullWidth
            label="Поиск новости"
            value={newsSearchTerm}
            onChange={(e) => setNewsSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isLoadingNews && allNews.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : allNews.length > 0 ? (
          <List
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
            }}
            onScroll={handleNewsScroll}
          >
            {allNews.map((newsItem) => (
              <ListItem key={newsItem.id} disablePadding>
                <ListItemButton
                  onClick={() => handleNewsSelection(newsItem.id)}
                  selected={selectedNewsId === newsItem.id}
                  sx={{
                    "&:hover": { bgcolor: "action.hover" },
                    "&.Mui-selected": { bgcolor: "primary.light" },
                  }}
                >
                  <ListItemText
                    primary={newsItem.title}
                    secondary={
                      newsItem.text
                        ? newsItem.text.length > 100
                          ? `${newsItem.text.substring(0, 100)}...`
                          : newsItem.text
                        : "Нет описания"
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {isLoadingMoreNews && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </List>
        ) : (
          <Typography color="text.secondary" align="center" py={3}>
            Новости не найдены
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setOpenNewsDialog(false);
            setNewsSearchTerm("");
          }}
        >
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEventsDialog = () => (
    <Dialog
      open={openEventsDialog}
      onClose={() => {
        setOpenEventsDialog(false);
        setEventsSearchTerm("");
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>Выберите мероприятие</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2,
        }}
      >
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <TextField
            fullWidth
            label="Поиск мероприятия"
            value={eventsSearchTerm}
            onChange={(e) => setEventsSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isLoadingEvents && allEvents.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : allEvents.length > 0 ? (
          <List
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
            }}
            onScroll={handleEventsScroll}
          >
            {allEvents.map((eventItem) => (
              <ListItem key={eventItem.id} disablePadding>
                <ListItemButton
                  onClick={() => handleEventSelection(eventItem.id)}
                  selected={selectedEventId === eventItem.id}
                  sx={{
                    "&:hover": { bgcolor: "action.hover" },
                    "&.Mui-selected": { bgcolor: "primary.light" },
                  }}
                >
                  <ListItemText
                    primary={eventItem.title}
                    secondary={
                      eventItem.city
                        ? `${eventItem.city.name} • ${format(
                            new Date(eventItem.startAt),
                            "dd.MM.yyyy HH:mm"
                          )}`
                        : format(
                            new Date(eventItem.startAt),
                            "dd.MM.yyyy HH:mm"
                          )
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {isLoadingMoreEvents && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </List>
        ) : (
          <Typography color="text.secondary" align="center" py={3}>
            Мероприятия не найдены
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setOpenEventsDialog(false);
            setEventsSearchTerm("");
          }}
        >
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление баннерами
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Поиск баннеров"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Сортировка</InputLabel>
            <Select
              value={sortField}
              label="Сортировка"
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <MenuItem value="title">Название</MenuItem>
              <MenuItem value="createdAt">Дата создания</MenuItem>
              <MenuItem value="startAt">Дата начала</MenuItem>
              <MenuItem value="endAt">Дата окончания</MenuItem>
              <MenuItem value="rang">Приоритет</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={sortOrder === "asc"}
                  onChange={() =>
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                  }
                />
              }
              label={sortOrder === "asc" ? "По возрастанию" : "По убыванию"}
            />
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Статус</InputLabel>
            <Select
              value={
                displayActive === undefined ? "" : displayActive.toString()
              }
              label="Статус"
              onChange={(e) => {
                const value = e.target.value;
                setDisplayActive(
                  value === "" ? undefined : value === "true" ? true : false
                );
              }}
            >
              <MenuItem value="">Все баннеры</MenuItem>
              <MenuItem value="true">Активные</MenuItem>
              <MenuItem value="false">Неактивные</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Paper sx={{ width: "100%", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography gutterBottom fontWeight={600} fontSize={18}>
              Баннеры
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Добавить
            </Button>
          </Box>

          {isLoadingBanners ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {renderBannerCards()}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  disabled={!pageToken}
                  onClick={handlePreviousBannersPage}
                >
                  Предыдущая
                </Button>
                <Button
                  variant="outlined"
                  disabled={!bannersData?.pageToken?.trim()}
                  onClick={handleNextBannersPage}
                >
                  Следующая
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
      {renderImagesDialog()}
      {renderTargetingDialog()}
      {renderNewsDialog()}
      {renderEventsDialog()}
      {openDeleteDialog && selectedBannerForAction && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
          <DialogTitle>Удалить баннер</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить баннер "
              {selectedBannerForAction.title}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Отмена</Button>
            <Button color="error" onClick={handleDeleteBanner}>
              Удалить
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export { BannersManagement };
